"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Spinner, Alert } from "reactstrap"
import { ExhibitorRegistrationService } from "../../../Service/exhibitor-registration-service"
import type { Exhibitor, VerifiedExhibitor } from "./types"
import { toast } from "react-toastify"
import VerifiedExhibitorsList from "./verified-exhibitors-list"
import ExhibitorSelectionDropdown from "./exhibitor-selection-dropdown"
import Swal from "sweetalert2"

// Main Exhibitor Selection Component with Two-Column Layout
interface ExhibitorSelectionProps {
  onExhibitorSelected: (exhibitor: Exhibitor) => void
  onVerifiedExhibitorSelected: (exhibitor: VerifiedExhibitor) => void
}

const ExhibitorSelection: React.FC<ExhibitorSelectionProps> = ({ onExhibitorSelected, onVerifiedExhibitorSelected }) => {
  const [exhibitors, setExhibitors] = useState<Exhibitor[]>([])
  const [verifiedExhibitors, setVerifiedExhibitors] = useState<VerifiedExhibitor[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const allExhibitors = await ExhibitorRegistrationService.getExhibitorsForSelection()
      setExhibitors(allExhibitors)

      try {
        const verifiedData = await ExhibitorRegistrationService.getVerifiedExhibitorsWithAccess()
        setVerifiedExhibitors(verifiedData.verified_exhibitors)
      } catch (error) {
        console.warn("Failed to load verified exhibitors:", error)
        setVerifiedExhibitors([])
      }
    } catch (error) {
      toast.error("Failed to load exhibitor data")
      console.error("Error loading exhibitors:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleExhibitorSelection = async (exhibitor: Exhibitor) => {
    setLoading(true)
    try {
      const response = await ExhibitorRegistrationService.directVerifyExhibitor({
        exhibitor_id: exhibitor.id,
      })

      if (response.success) {
        await Swal.fire({
          title: "Verification Successful!",
          html: `
            <div class="text-start">
              <p><strong>Company:</strong> ${response.exhibitor.name}</p>
              <p><strong>Status:</strong> <span class="text-success">Verified</span></p>
              <p><strong>Verified At:</strong> ${new Date(response.verification.verified_at).toLocaleString()}</p>
              <p class="text-muted mt-3">You now have access to manage staff and cars for this company.</p>
            </div>
          `,
          icon: "success",
          confirmButtonColor: "#28a745",
          confirmButtonText: "Continue to Dashboard",
          allowOutsideClick: false,
        })

        const verifiedExhibitor: Exhibitor = {
          ...exhibitor,
          ...response.exhibitor,
        }

        toast.success(`Successfully verified access to ${response.exhibitor.name}`)
        onExhibitorSelected(verifiedExhibitor)
      } else {
        throw new Error(response.message || "Verification failed")
      }
    } catch (error: any) {
      console.error("Error verifying exhibitor:", error)

      const errorMessage = error.response?.data?.message || error.message || "Failed to verify company access"

      await Swal.fire({
        title: "Verification Failed",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#dc3545",
        confirmButtonText: "Try Again",
      })

      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  if (loading && exhibitors.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" className="mb-3" />
        <p className="text-muted">Loading your companies...</p>
      </div>
    )
  }

  return (
    <div className="container-fluid py-3">
      <div className="row g-3">
        {/* Left Column - Selection */}
        <div className="col-12 col-lg-5">
          <ExhibitorSelectionDropdown
            exhibitors={exhibitors}
            onExhibitorSelected={handleExhibitorSelection}
            loading={loading}
          />
        </div>
        
        {/* Right Column - Verified Companies List */}
        <div className="col-12 col-lg-7">
          <VerifiedExhibitorsList
            verifiedExhibitors={verifiedExhibitors}
            onSelectExhibitor={onVerifiedExhibitorSelected}
          />
        </div>
      </div>
    </div>
  )
}

export default ExhibitorSelection